/* Refresh only the advice after Prestige filters change; preserve any inquiry draft. */
(() => {
  if (customElements.get('jacc-collection-guidance')) return;

  class JaccCollectionGuidance extends HTMLElement {
    connectedCallback() {
      if (this.listening) return;
      this.listening = true;
      this.handleFacet = (event) => this.refresh(event.detail?.url || location.href);
      this.handleHistory = () => this.refresh(location.href);
      this.handleSubmit = () => this.updateContext(this.currentURL || location.href);
      document.addEventListener('facet:update', this.handleFacet);
      window.addEventListener('popstate', this.handleHistory);
      this.addEventListener('submit', this.handleSubmit);
      this.updateContext(location.href);
      const result = this.querySelector('[data-guidance-result]');
      if (result) {
        this.querySelector('.jacc-guidance__contact').open = true;
        result.focus({ preventScroll: true });
        result.scrollIntoView({ behavior: 'auto', block: 'center' });
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      document.removeEventListener('facet:update', this.handleFacet);
      window.removeEventListener('popstate', this.handleHistory);
      this.removeEventListener('submit', this.handleSubmit);
      this.listening = false;
    }

    selectionURL(value) {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin) throw new Error('Unexpected collection origin');
      // Retain every repeated filter, price bound, sorting choice and Finder parameter.
      // Only rendering parameters and tracking data are removed from the inquiry link.
      for (const key of [...url.searchParams.keys()]) {
        if (key === 'section_id' || key === 'sections' || key === 'contact_posted' ||
            key.startsWith('utm_') || ['gclid', 'fbclid', '_pos', '_psq', '_ss', '_v', '_sid'].includes(key)) {
          url.searchParams.delete(key);
        }
      }
      url.hash = '';
      return url;
    }

    updateContext(value) {
      const url = this.selectionURL(value);
      this.currentURL = url.href;
      const field = this.querySelector('[data-guidance-context-url]');
      if (field) field.value = url.href;
      const returnField = this.querySelector('input[name="return_to"]');
      const contact = this.querySelector('.jacc-guidance__contact');
      if (returnField && contact) {
        // This destination is used after Shopify accepts the contact submission.
        // Keep the success flag on the return URL, not on the emailed collection link.
        const successURL = new URL(url.href);
        successURL.searchParams.set('contact_posted', 'true');
        successURL.hash = contact.id;
        returnField.value = successURL.pathname + successURL.search + successURL.hash;
      }
    }

    async refresh(value) {
      const url = this.selectionURL(value);
      if (!url.pathname.includes('/collections/')) return;
      this.updateContext(url);
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const editorial = this.querySelector('[data-guidance-editorial]');
      editorial?.setAttribute('aria-busy', 'true');
      url.searchParams.set('section_id', this.dataset.sectionId);
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(url.href, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Guidance could not be loaded');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const updated = doc.querySelector('[data-guidance-editorial]');
        if (!updated) throw new Error('Guidance was missing');
        if (this.controller !== controller || !this.isConnected) return;
        this.querySelector('[data-guidance-editorial]')?.replaceWith(document.importNode(updated, true));
      } catch (error) {
        if (this.controller !== controller || !this.isConnected) return;
        // A failed refresh must not leave mechanical advice above quartz results.
        const current = this.querySelector('[data-guidance-editorial]');
        current?.classList.add('jacc-guidance__editorial--personal');
        current?.querySelector('.jacc-guidance__answers')?.remove();
        const heading = current?.querySelector('h2');
        const intro = current?.querySelector('.jacc-guidance__intro');
        if (heading) heading.textContent = 'A question about this selection?';
        if (intro) intro.textContent = 'Tell me which model caught your eye and what you’d like to know. I’m happy to help.';
      } finally {
        clearTimeout(timeout);
        if (this.controller === controller) this.querySelector('[data-guidance-editorial]')?.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('jacc-collection-guidance', JaccCollectionGuidance);
})();
